/**
 * Kaufmännische Bereiche: Kunden, Produkte, Rechnungen, Tickets, Finanzen, Personal.
 */
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import type {
  Contract,
  Customer,
  Invoice,
  Loan,
  LoanProductTemplate,
  Product,
  ProductKind,
  ServerId,
  ShiftModel,
  Staff,
  StaffRole,
  Ticket,
  TicketCategory,
  TicketPriority,
} from '@hosting-sim/core';
import {
  DEFAULT_SPECIALTIES,
  RESPONSE_SLA,
  SHIFT_LABEL,
  SIM,
  closableOutageTickets,
  contractUptimePercent,
  effectiveLoanRate,
  estimateMigration,
  hasTicketContext,
  migrationTargets,
  serverCapacity,
  supportCapacity,
  ticketServices,
} from '@hosting-sim/core';
import { Link } from 'react-router-dom';

/** Beschriftung der Ticketkategorien – auch für Fachgebiete des Personals. */
const TICKET_CATEGORY_LABEL: Record<TicketCategory, string> = {
  technical: 'Technik',
  billing: 'Abrechnung',
  abuse: 'Missbrauch',
  sales: 'Vertrieb',
  outage: 'Störungen',
};
import {
  Badge,
  Button,
  Card,
  Column,
  DataTable,
  EmptyState,
  Field,
  Input,
  Meter,
  Modal,
  Mono,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  StatusDot,
  Tabs,
  type Tone,
} from '../components/ui';
import { BarBreakdown, LineChart } from '../components/charts/Charts';
import { useGame, useMetrics, useUnlocked } from '../store/game';
import { api } from '../lib/api';
import { bytes, dec, duration, gameDate, mbps, money, num, pct, pct100, shortId } from '../lib/format';

// ---------------------------------------------------------------------------
// Kunden
// ---------------------------------------------------------------------------

const SEGMENT_LABEL: Record<Customer['segment'], string> = {
  private: 'Privat',
  smb: 'Mittelstand',
  enterprise: 'Großkunde',
  reseller: 'Reseller',
};

function satisfactionTone(value: number): Tone {
  return value >= 70 ? 'success' : value >= 45 ? 'warning' : 'danger';
}

export function Customers() {
  const world = useGame((s) => s.world);
  const [selected, setSelected] = useState<Customer | null>(null);
  const [search, setSearch] = useState('');

  const customers = useMemo(() => {
    if (!world) return [];
    const all = Object.values(world.customers);
    if (!search.trim()) return all;
    const needle = search.toLowerCase();
    return all.filter((c) => (c.company ?? c.name).toLowerCase().includes(needle) || c.email.toLowerCase().includes(needle));
  }, [world, search]);

  if (!world) return null;

  const columns: Column<Customer>[] = [
    {
      key: 'name',
      header: 'Kunde',
      sortValue: (c) => c.company ?? c.name,
      render: (c) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{c.company ?? c.name}</p>
          <p className="truncate text-2xs text-panel-500">{c.email}</p>
        </div>
      ),
    },
    { key: 'segment', header: 'Segment', hideOnMobile: true, sortValue: (c) => c.segment, render: (c) => <Badge tone="muted">{SEGMENT_LABEL[c.segment]}</Badge> },
    {
      key: 'contracts',
      header: 'Verträge',
      align: 'right',
      sortValue: (c) => c.contractIds.length,
      render: (c) => <span className="tabular text-xs">{c.contractIds.filter((id) => world.contracts[id]?.state === 'active').length}</span>,
    },
    {
      key: 'revenue',
      header: 'Umsatz/Monat',
      align: 'right',
      sortValue: (c) => c.contractIds.reduce((s, id) => s + (world.contracts[id]?.monthlyPrice ?? 0), 0),
      render: (c) => <span className="tabular text-xs">{money(c.contractIds.reduce((s, id) => s + (world.contracts[id]?.monthlyPrice ?? 0), 0))}</span>,
    },
    {
      key: 'satisfaction',
      header: 'Zufriedenheit',
      align: 'right',
      sortValue: (c) => c.satisfaction,
      render: (c) => <StatusDot tone={satisfactionTone(c.satisfaction)} label={`${Math.round(c.satisfaction)}`} />,
    },
    {
      key: 'balance',
      header: 'Saldo',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => c.balance,
      render: (c) => <span className={`tabular text-xs ${c.balance < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{money(c.balance)}</span>,
    },
    {
      key: 'churn',
      header: 'Kündigungsrisiko',
      align: 'right',
      hideOnMobile: true,
      sortValue: (c) => c.churnRisk,
      render: (c) => (
        <span className={`tabular text-xs ${c.churnRisk > 0.5 ? 'text-red-600 dark:text-red-400' : c.churnRisk > 0.2 ? 'text-amber-600' : ''}`}>
          {Math.round(c.churnRisk * 100)} %
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Kunden"
        description={`${num(Object.keys(world.customers).length)} Kundenkonten`}
        actions={<Input placeholder="Suchen …" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" />}
      />

      <Card padded={false}>
        <DataTable columns={columns} rows={customers} rowKey={(c) => c.id} onRowClick={setSelected} empty="Noch keine Kunden gewonnen." />
      </Card>

      {selected && <CustomerDetail customer={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function CustomerDetail({ customer, onClose }: { customer: Customer; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [credit, setCredit] = useState('10');
  const [moving, setMoving] = useState<Contract | null>(null);

  if (!world) return null;
  const current = world.customers[customer.id] ?? customer;
  const contracts = current.contractIds.map((id) => world.contracts[id]).filter(Boolean) as Contract[];
  const invoices = current.invoiceIds.map((id) => world.invoices[id]).filter(Boolean) as Invoice[];
  const tickets = current.ticketIds.map((id) => world.tickets[id]).filter(Boolean) as Ticket[];

  return (
    <Modal open onClose={onClose} wide title={current.company ?? current.name}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Ansprechpartner">{current.name}</Field>
          <Field label="E-Mail">{current.email}</Field>
          <Field label="Segment">{SEGMENT_LABEL[current.segment]}</Field>
          <Field label="Kunde seit">Tick {num(current.joinedAtTick)}</Field>
          <Field label="Zufriedenheit">
            <StatusDot tone={satisfactionTone(current.satisfaction)} label={`${Math.round(current.satisfaction)} / 100`} />
          </Field>
          <Field label="Zahlungsmoral">{pct100(current.reliability * 100, 0)}</Field>
          <Field label="Saldo">{money(current.balance)}</Field>
          <Field label="Kündigungsrisiko">{Math.round(current.churnRisk * 100)} %</Field>
        </div>

        <div>
          <p className="label mb-1.5">Verträge</p>
          {contracts.length === 0 ? (
            <p className="text-xs text-panel-500">Keine Verträge.</p>
          ) : (
            <div className="space-y-1.5">
              {contracts.map((contract) => {
                const product = world.products[contract.productId];
                const uptime = contractUptimePercent(contract);
                return (
                  <div key={contract.id} className="surface-muted flex flex-wrap items-center gap-3 p-2.5 text-xs">
                    <Badge tone={contract.state === 'active' ? 'success' : contract.state === 'suspended' ? 'warning' : 'muted'}>
                      {contract.state === 'active' ? 'aktiv' : contract.state === 'suspended' ? 'gesperrt' : 'beendet'}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate font-medium">{product?.name ?? contract.productId}</span>
                    <span className="tabular">{money(contract.monthlyPrice)}/Mon.</span>
                    <span className="tabular text-panel-500">Verfügbar {pct100(uptime, 2)}</span>
                    {contract.ipv4.length > 0 && <Mono className="text-panel-500">{contract.ipv4[0]}</Mono>}
                    {contract.state === 'active' && (
                      <Button size="sm" variant="ghost" onClick={() => void run({ type: 'contract/suspend', contractId: contract.id })}>
                        sperren
                      </Button>
                    )}
                    {contract.state === 'suspended' && (
                      <Button size="sm" variant="ghost" onClick={() => void run({ type: 'contract/resume', contractId: contract.id })}>
                        entsperren
                      </Button>
                    )}
                    {contract.serverId && !contract.vpsId && contract.state !== 'cancelled' && (
                      <Button size="sm" variant="ghost" onClick={() => setMoving(contract)}>
                        Server wechseln
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="label mb-1.5">Letzte Rechnungen</p>
            {invoices.length === 0 ? (
              <p className="text-xs text-panel-500">Noch keine Rechnung gestellt.</p>
            ) : (
              <ul className="space-y-1">
                {invoices.slice(-5).reverse().map((invoice) => (
                  <li key={invoice.id} className="flex items-center justify-between gap-2 text-xs">
                    <Mono>{invoice.number}</Mono>
                    <span className="tabular">{money(invoice.gross)}</span>
                    <Badge tone={invoice.state === 'paid' ? 'success' : invoice.state === 'overdue' ? 'danger' : 'neutral'}>{invoice.state}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="label mb-1.5">Tickets</p>
            {tickets.length === 0 ? (
              <p className="text-xs text-panel-500">Keine Anfragen.</p>
            ) : (
              <ul className="space-y-1">
                {tickets.slice(-5).reverse().map((ticket) => (
                  <li key={ticket.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="min-w-0 flex-1 truncate">{ticket.subject}</span>
                    <Badge tone={ticket.state === 'resolved' || ticket.state === 'closed' ? 'success' : 'warning'}>{ticket.state}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-end gap-2 border-t divider pt-3">
          <Input label="Gutschrift (€)" value={credit} onChange={(e) => setCredit(e.target.value)} className="w-32" />
          <Button
            onClick={() =>
              void run({ type: 'customer/credit', customerId: current.id, amount: Number(credit) || 0, reason: 'Kulanz' })
            }
          >
            Gutschreiben
          </Button>
          <span className="pb-1.5 text-xs text-panel-500">Eine Gutschrift hebt die Zufriedenheit spürbar an.</span>
        </div>
      </div>

      {moving && <MoveContractDialog contract={moving} onClose={() => setMoving(null)} />}
    </Modal>
  );
}

/**
 * Umzug eines einzelnen Vertrags auf einen anderen Server – direkt aus der
 * Kundenkarte heraus, ohne den Umweg über die Umzugsseite und deren Liste
 * aller VPS-Instanzen und Verträge im Spiel.
 */
function MoveContractDialog({ contract, onClose }: { contract: Contract; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [target, setTarget] = useState<ServerId | ''>('');
  const [busy, setBusy] = useState(false);

  if (!world) return null;
  const current = world.contracts[contract.id] ?? contract;
  const product = world.products[current.productId];
  const source = current.serverId ? world.servers[current.serverId] : undefined;

  const targetIds = migrationTargets(world, current.id);
  const targets = targetIds.map((id) => world.servers[id]).filter((s): s is NonNullable<typeof s> => !!s);

  const estimate = target ? estimateMigration(world, { kind: 'contract', id: current.id, targetServerId: target }) : null;

  const start = async () => {
    if (!target) return;
    setBusy(true);
    const result = await run({ type: 'contract/migrate', contractId: current.id, targetServerId: target });
    setBusy(false);
    if (result.ok) onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Server wechseln – ${product?.name ?? current.productId}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={busy} disabled={!target || (estimate ? !estimate.ok : false)} onClick={() => void start()}>
            Umzug starten
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Aktueller Server">{source?.name ?? 'Unbekannt'}</Field>

        <Select
          label="Neuer Server"
          value={target}
          onChange={(e) => setTarget(e.target.value as ServerId)}
          hint={targets.length === 0 ? 'Kein passender Server verfügbar – Rolle, Betriebssystem, Verkabelung und Kapazität müssen stimmen.' : undefined}
        >
          <option value="">– bitte wählen –</option>
          {targets.map((s) => {
            const capacity = serverCapacity(world, s.id);
            return (
              <option key={s.id} value={s.id}>
                {s.name} — {pct(capacity.ramGbUsed / Math.max(1, capacity.ramGb), 0)} belegt, {world.datacenters[s.datacenterId]?.city}
              </option>
            );
          })}
        </Select>

        {estimate && !estimate.ok && (
          <Notice tone="warning" title="Umzug nicht möglich">
            {estimate.error}
          </Notice>
        )}

        {estimate && estimate.ok && (
          <Notice tone={estimate.seamless ? 'info' : 'warning'} title={estimate.seamless ? 'Läuft ohne Unterbrechung' : 'Umschaltpause erwartet'}>
            {Math.round(estimate.diskGb)} GB über {mbps(estimate.rateMbps)} – etwa {duration(estimate.ticks)}.{' '}
            {estimate.seamless
              ? 'Der Vertrag liegt in einem Pool, der Kunde merkt nichts davon.'
              : `Beim Umschalten fällt der Dienst rund ${Math.round(estimate.freezeSeconds / 60)} Minuten aus.`}
          </Notice>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Produkte
// ---------------------------------------------------------------------------

const PRODUCT_KIND_LABEL: Record<ProductKind, string> = {
  'shared-hosting': 'Shared Hosting',
  vps: 'VPS',
  dedicated: 'Dedicated',
  colocation: 'Colocation',
  storage: 'Storage',
  domain: 'Domain',
  ssl: 'SSL',
  backup: 'Backup',
  mail: 'Mail',
  'transit-sale': 'Transit',
  'game-server': 'Game-Server',
  container: 'Container',
  'gpu-compute': 'GPU-Compute',
};

export function Products() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [editing, setEditing] = useState<Product | null>(null);
  const [price, setPrice] = useState('');

  if (!world) return null;
  const products = Object.values(world.products);
  const contractsByProduct = new Map<string, number>();
  for (const contract of Object.values(world.contracts)) {
    if (contract.state !== 'active') continue;
    contractsByProduct.set(contract.productId, (contractsByProduct.get(contract.productId) ?? 0) + 1);
  }

  const columns: Column<Product>[] = [
    {
      key: 'name',
      header: 'Produkt',
      sortValue: (p) => p.name,
      render: (p) => (
        <div>
          <p className="font-medium">{p.name}</p>
          <p className="text-2xs text-panel-500">{PRODUCT_KIND_LABEL[p.kind]}</p>
        </div>
      ),
    },
    {
      key: 'active',
      header: 'Verkauf',
      align: 'center',
      render: (p) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            void run({ type: 'product/setActive', productId: p.id, active: !p.active });
          }}
        >
          {p.active ? <Badge tone="success">aktiv</Badge> : <Badge tone="muted">pausiert</Badge>}
        </Button>
      ),
    },
    { key: 'price', header: 'Preis/Monat', align: 'right', sortValue: (p) => p.monthlyPrice, render: (p) => <span className="tabular font-medium">{money(p.monthlyPrice)}</span> },
    {
      key: 'attract',
      header: 'Nachfrage',
      align: 'right',
      sortValue: (p) => p.attractiveness,
      render: (p) => (
        <span className={`tabular text-xs ${p.attractiveness > 1.2 ? 'text-accent-600 dark:text-accent-400' : p.attractiveness < 0.6 ? 'text-red-600 dark:text-red-400' : ''}`}>
          {dec(p.attractiveness, 2)}×
        </span>
      ),
    },
    { key: 'sold', header: 'Verkauft', align: 'right', sortValue: (p) => contractsByProduct.get(p.id) ?? 0, render: (p) => <span className="tabular">{num(contractsByProduct.get(p.id) ?? 0)}</span> },
    {
      key: 'revenue',
      header: 'Umsatz/Monat',
      align: 'right',
      sortValue: (p) => (contractsByProduct.get(p.id) ?? 0) * p.monthlyPrice,
      render: (p) => <span className="tabular">{money((contractsByProduct.get(p.id) ?? 0) * p.monthlyPrice)}</span>,
    },
    {
      key: 'res',
      header: 'Ressourcen',
      hideOnMobile: true,
      render: (p) => (
        <span className="text-2xs text-panel-500">
          {p.resources.cpuCores > 0 && `${dec(p.resources.cpuCores, 2)} Kerne · `}
          {p.resources.ramGb > 0 && `${bytes(p.resources.ramGb)} · `}
          {p.resources.ssdGb > 0 && `${bytes(p.resources.ssdGb)} SSD`}
          {p.resources.ipv4Count > 0 && ` · ${p.resources.ipv4Count} IPv4`}
          {!!p.resources.gpuCount && ` · ${p.resources.gpuCount} GPU`}
        </span>
      ),
    },
    { key: 'sla', header: 'SLA', align: 'right', hideOnMobile: true, render: (p) => <span className="tabular text-xs">{dec(p.slaUptimePercent, 2)} %</span> },
  ];

  return (
    <>
      <PageHeader title="Produkte" description="Sortiment und Preisgestaltung. Der Preis steuert die Nachfrage unmittelbar." />

      <Notice tone="info" title="So wirkt der Preis">
        Der Nachfragefaktor vergleicht den eigenen Preis mit dem Marktreferenzpreis. Ein Wert über 1 bedeutet: günstiger als der Markt, also
        mehr Anfragen – aber auch mehr Kunden pro Server und mehr Supportaufwand.
      </Notice>

      <Card className="mt-4" padded={false}>
        <DataTable
          columns={columns}
          rows={products}
          rowKey={(p) => p.id}
          onRowClick={(p) => {
            setEditing(p);
            setPrice(String(p.monthlyPrice));
          }}
          empty="Keine Produkte."
        />
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={`${editing?.name} – Preis anpassen`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                const result = await run({ type: 'product/setPrice', productId: editing!.id, monthlyPrice: Number(price) || 0 });
                if (result.ok) setEditing(null);
              }}
            >
              Speichern
            </Button>
          </>
        }
      >
        <Input
          label="Monatspreis in Euro"
          type="number"
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          hint="Bestandsverträge behalten ihren vereinbarten Preis; die Änderung gilt für Neuabschlüsse."
        />
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Rechnungen
// ---------------------------------------------------------------------------

export function Invoices() {
  const world = useGame((s) => s.world);
  const [filter, setFilter] = useState('all');

  if (!world) return null;
  const all = Object.values(world.invoices);
  const invoices = filter === 'all' ? all : all.filter((i) => i.state === filter);

  const columns: Column<Invoice>[] = [
    { key: 'number', header: 'Nummer', sortValue: (i) => i.number, render: (i) => <Mono className="font-medium">{i.number}</Mono> },
    {
      key: 'customer',
      header: 'Kunde',
      sortValue: (i) => world.customers[i.customerId]?.name ?? '',
      render: (i) => {
        const customer = world.customers[i.customerId];
        return <span className="truncate">{customer ? (customer.company ?? customer.name) : shortId(i.customerId)}</span>;
      },
    },
    {
      key: 'state',
      header: 'Status',
      sortValue: (i) => i.state,
      render: (i) => (
        <Badge tone={i.state === 'paid' ? 'success' : i.state === 'overdue' ? 'danger' : i.state === 'open' ? 'warning' : 'muted'}>
          {i.state === 'paid' ? 'bezahlt' : i.state === 'overdue' ? 'überfällig' : i.state === 'open' ? 'offen' : i.state}
        </Badge>
      ),
    },
    { key: 'net', header: 'Netto', align: 'right', hideOnMobile: true, sortValue: (i) => i.net, render: (i) => <span className="tabular">{money(i.net)}</span> },
    { key: 'tax', header: 'USt.', align: 'right', hideOnMobile: true, sortValue: (i) => i.tax, render: (i) => <span className="tabular">{money(i.tax)}</span> },
    { key: 'gross', header: 'Brutto', align: 'right', sortValue: (i) => i.gross, render: (i) => <span className="tabular font-medium">{money(i.gross)}</span> },
    { key: 'items', header: 'Positionen', align: 'right', hideOnMobile: true, render: (i) => <span className="tabular text-xs">{i.items.length}</span> },
    { key: 'due', header: 'Fällig', align: 'right', hideOnMobile: true, sortValue: (i) => i.dueAtTick, render: (i) => <span className="tabular text-xs">Tick {num(i.dueAtTick)}</span> },
  ];

  const open = all.filter((i) => i.state === 'open');
  const overdue = all.filter((i) => i.state === 'overdue');
  const paid = all.filter((i) => i.state === 'paid');

  return (
    <>
      <PageHeader title="Rechnungen" description="Monatliche Abrechnung inklusive Trafficüberverbrauch und SLA-Gutschriften." />

      <StatGrid cols={4}>
        <Stat label="Offen" value={num(open.length)} hint={money(open.reduce((s, i) => s + i.gross, 0))} />
        <Stat label="Überfällig" value={num(overdue.length)} tone={overdue.length ? 'negative' : 'neutral'} hint={money(overdue.reduce((s, i) => s + i.gross, 0))} />
        <Stat label="Bezahlt" value={num(paid.length)} tone="positive" hint={money(paid.reduce((s, i) => s + i.gross, 0))} />
        <Stat label="Durchschnittsbetrag" value={money(all.length ? all.reduce((s, i) => s + i.gross, 0) / all.length : 0)} />
      </StatGrid>

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'all', label: 'Alle' },
            { id: 'open', label: 'Offen' },
            { id: 'overdue', label: 'Überfällig' },
            { id: 'paid', label: 'Bezahlt' },
          ]}
          active={filter}
          onChange={setFilter}
        />
      </div>

      <Card className="mt-4" padded={false}>
        <DataTable columns={columns} rows={invoices} rowKey={(i) => i.id} dense empty="Rechnungen entstehen zum Monatswechsel." />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------

const PRIORITY_TONE: Record<TicketPriority, Tone> = { urgent: 'danger', high: 'warning', normal: 'neutral', low: 'muted' };
const PRIORITY_LABEL: Record<TicketPriority, string> = { urgent: 'dringend', high: 'hoch', normal: 'normal', low: 'niedrig' };

/**
 * Leiste über der Tabelle: Auswahl und Schnellzugriffe.
 *
 * Der wichtigste Knopf ist der erste. Nach einem Ausfall liegen hunderte
 * gleichlautende Störungsmeldungen an, deren Ursache längst behoben ist – die
 * lassen sich sauber in einem Rutsch schließen. Alles andere pauschal
 * zuzumachen ist möglich, aber ausdrücklich als das gekennzeichnet, was es ist.
 */
function BulkBar({
  selectedIds,
  visible,
  onClear,
  onOpenDialog,
}: {
  selectedIds: string[];
  visible: Ticket[];
  onClear: () => void;
  onOpenDialog: () => void;
}) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const closable = useMemo(() => (world ? closableOutageTickets(world) : []), [world]);

  if (!world) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {closable.length > 0 && (
        <Button
          variant="primary"
          size="sm"
          title="Störungsmeldungen, deren Ursache behoben ist – eine Statusmeldung an alle Betroffenen."
          onClick={() =>
            void run({
              type: 'ticket/bulk',
              ticketIds: closable.map((t) => t.id),
              body: 'Die Störung ist behoben. Wir bitten die Unterbrechung zu entschuldigen.',
              resolve: true,
            })
          }
        >
          {closable.length} behobene Störungen schließen
        </Button>
      )}

      {selectedIds.length > 0 ? (
        <>
          <span className="text-xs text-panel-500 dark:text-panel-400">{selectedIds.length} ausgewählt</span>
          <Button size="sm" onClick={onOpenDialog}>
            Auswahl bearbeiten
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear}>
            Auswahl aufheben
          </Button>
        </>
      ) : (
        visible.length > 0 && (
          <span className="text-xs text-panel-500 dark:text-panel-400">
            Vorgänge ankreuzen, um sie gemeinsam zu bearbeiten.
          </span>
        )
      )}
    </div>
  );
}

/** Sammelantwort mit ehrlicher Ansage, was das kostet. */
function BulkDialog({
  ticketIds,
  onClose,
  onDone,
}: {
  ticketIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [body, setBody] = useState('Wir haben Ihr Anliegen geprüft und den Vorgang abgeschlossen.');
  const [resolve, setResolve] = useState(true);
  const [busy, setBusy] = useState(false);

  const tickets = useMemo(
    () => ticketIds.map((id) => world?.tickets[id as never]).filter(Boolean) as Ticket[],
    [ticketIds, world],
  );
  const outages = tickets.filter((t) => t.category === 'outage');
  const regular = tickets.filter((t) => t.category !== 'outage');

  return (
    <Modal
      open
      onClose={onClose}
      title={`${ticketIds.length} Vorgänge bearbeiten`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant={resolve && regular.length > 0 ? 'danger' : 'primary'}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const result = await run({ type: 'ticket/bulk', ticketIds: ticketIds as never, body, resolve });
                if (result.ok) onDone();
              } finally {
                setBusy(false);
              }
            }}
          >
            {resolve ? 'Antworten und schließen' : 'Nur antworten'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="label">Nachricht an die Kunden</span>
          <textarea
            className="mt-1 w-full rounded-md border border-panel-300 bg-white p-2 text-sm dark:border-panel-700 dark:bg-panel-900"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={resolve} onChange={(e) => setResolve(e.target.checked)} />
          Vorgänge dabei schließen
        </label>

        {outages.length > 0 && (
          <Notice tone="info" title={`${outages.length} Störungsmeldungen`}>
            Meldungen, deren Ursache behoben ist, werden sauber geschlossen. Wo die Störung noch besteht, bleibt der Vorgang
            offen – ihn zu schließen wäre gegenüber dem Kunden schlicht unwahr.
          </Notice>
        )}

        {resolve && regular.length > 0 && (
          <Notice tone="warning" title={`${regular.length} reguläre Anfragen werden ungeprüft geschlossen`}>
            Ohne Bearbeiter gibt es keinen Zufriedenheitsgewinn, und ein Teil dieser Vorgänge wird in den nächsten Tagen wieder
            aufgemacht – dann mit höherer Priorität. Für einen echten Abbau hilft nur mehr Supportkapazität.
          </Notice>
        )}
      </div>
    </Modal>
  );
}

export function Tickets() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const unlocked = useUnlocked();
  const [filter, setFilter] = useState('open');
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  if (!world) return null;
  const all = Object.values(world.tickets);
  const tickets = filter === 'all' ? all : filter === 'open' ? all.filter((t) => t.state === 'open' || t.state === 'in-progress') : all.filter((t) => t.state === filter);
  const akte = hasTicketContext(world);
  const forschungOffen = unlocked('research');

  const toggle = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const columns: Column<Ticket>[] = [
    {
      key: 'pick',
      header: (
        <input
          type="checkbox"
          aria-label="Alle auswählen"
          checked={tickets.length > 0 && tickets.every((t) => checked.has(t.id))}
          onChange={(e) =>
            setChecked(e.target.checked ? new Set(tickets.map((t) => t.id)) : new Set())
          }
        />
      ),
      width: '2.5rem',
      render: (t) => (
        <input
          type="checkbox"
          aria-label={`${t.subject} auswählen`}
          checked={checked.has(t.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggle(t.id)}
        />
      ),
    },
    {
      key: 'subject',
      header: 'Betreff',
      sortValue: (t) => t.subject,
      render: (t) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{t.subject}</p>
          <p className="truncate text-2xs text-panel-500">
            {t.customerId ? (world.customers[t.customerId]?.company ?? world.customers[t.customerId]?.name ?? '–') : 'System'}
          </p>
        </div>
      ),
    },
    { key: 'priority', header: 'Priorität', sortValue: (t) => t.priority, render: (t) => <Badge tone={PRIORITY_TONE[t.priority]}>{PRIORITY_LABEL[t.priority]}</Badge> },
    /*
     * Ohne die Ticketakte gibt es diese Spalte nicht – und zwar wirklich nicht,
     * nicht als graues „gesperrt“. Eine dauerhaft leere Spalte ist eine
     * Erinnerung an etwas Fehlendes, kein Hinweis. Der Hinweis steht einmal
     * über der Liste, wo er gelesen wird.
     */
    ...(akte
      ? [
          {
            key: 'betrifft',
            header: 'Betrifft',
            hideOnMobile: true,
            sortValue: (t: Ticket) => ticketServices(world, t)[0]?.serverName ?? '',
            render: (t: Ticket) => <TicketSubjectCell ticket={t} />,
          } as Column<Ticket>,
        ]
      : []),
    { key: 'category', header: 'Kategorie', hideOnMobile: true, sortValue: (t) => t.category, render: (t) => <span className="text-xs">{t.category}</span> },
    {
      key: 'state',
      header: 'Status',
      sortValue: (t) => t.state,
      render: (t) => (
        <Badge tone={t.state === 'resolved' || t.state === 'closed' ? 'success' : t.state === 'in-progress' ? 'info' : 'warning'}>{t.state}</Badge>
      ),
    },
    {
      key: 'age',
      header: 'Alter',
      align: 'right',
      sortValue: (t) => world.meta.tick - t.openedAtTick,
      render: (t) => {
        const age = world.meta.tick - t.openedAtTick;
        const overdue = t.firstResponseTick === null && age > RESPONSE_SLA[t.priority];
        return <span className={`tabular text-xs ${overdue ? 'font-semibold text-red-600 dark:text-red-400' : ''}`}>{duration(age)}</span>;
      },
    },
    {
      key: 'response',
      header: 'Erste Antwort',
      align: 'right',
      hideOnMobile: true,
      render: (t) => (t.firstResponseTick !== null ? <span className="tabular text-xs">{duration(t.firstResponseTick - t.openedAtTick)}</span> : <span className="text-2xs text-amber-600">ausstehend</span>),
    },
  ];

  const openTickets = all.filter((t) => t.state === 'open' || t.state === 'in-progress');
  const overdue = openTickets.filter((t) => t.firstResponseTick === null && world.meta.tick - t.openedAtTick > RESPONSE_SLA[t.priority]);
  const supportStaff = Object.values(world.staff).filter((s) => s.role === 'support');

  return (
    <>
      <PageHeader title="Tickets" description="Kundenanfragen und Störungsmeldungen." />

      <StatGrid cols={4}>
        <Stat label="Offen" value={num(openTickets.length)} tone={openTickets.length > 50 ? 'warning' : 'neutral'} />
        <Stat label="Reaktionszeit überschritten" value={num(overdue.length)} tone={overdue.length > 0 ? 'negative' : 'positive'} />
        <Stat label="Dringend" value={num(openTickets.filter((t) => t.priority === 'urgent').length)} />
        <Stat
          label="Supportkapazität"
          value={`${supportStaff.length} Mitarbeitende`}
          hint={supportStaff.length === 0 ? 'Ohne Support bleibt jedes Ticket liegen.' : `rund ${num(supportStaff.length * 45)} Vorgänge/Tag`}
          tone={supportStaff.length === 0 ? 'negative' : 'neutral'}
        />
      </StatGrid>

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'open', label: 'Offen', badge: <Badge tone="warning">{openTickets.length}</Badge> },
            { id: 'resolved', label: 'Gelöst' },
            { id: 'all', label: 'Alle' },
          ]}
          active={filter}
          onChange={setFilter}
        />
      </div>

      {!akte && openTickets.length > 0 && (
        <div className="mt-3">
          <Notice tone="info" title="Wer meldet sich da eigentlich?">
            Ein Ticket sagt „Website langsam“ – auf welchem Knoten die Website liegt, steht nirgends. Die Forschung{' '}
            {/*
              Nur verlinken, was es auch gibt: Der Forschungsbereich schaltet
              sich erst mit dem dreistelligen Kundenstamm frei. Ein Verweis
              dorthin wäre vorher ein Knopf ins Leere – und lässt den Spieler
              nach einem Reiter suchen, den es noch nicht gibt.
            */}
            {forschungOffen ? (
              <Link className="underline" to="/research">
                Support-Werkzeuge
              </Link>
            ) : (
              <>„Support-Werkzeuge“</>
            )}{' '}
            führt Kundenkonto und Serverbestand zusammen; danach steht an jedem Vorgang, welches Produkt betroffen ist und wo
            es läuft.
            {!forschungOffen && ' Der Forschungsbereich öffnet sich mit dem hundertsten Kunden.'}
          </Notice>
        </div>
      )}

      <BulkBar
        selectedIds={[...checked]}
        visible={tickets}
        onClear={() => setChecked(new Set())}
        onOpenDialog={() => setBulkOpen(true)}
      />

      <Card className="mt-3" padded={false}>
        <DataTable columns={columns} rows={tickets} rowKey={(t) => t.id} onRowClick={setSelected} dense empty="Keine Tickets in dieser Ansicht." />
      </Card>

      {bulkOpen && (
        <BulkDialog
          ticketIds={[...checked]}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false);
            setChecked(new Set());
          }}
        />
      )}

      {selected && (
        <Modal
          open
          onClose={() => setSelected(null)}
          wide
          title={selected.subject}
          footer={
            <>
              <Button variant="ghost" onClick={() => setSelected(null)}>
                Schließen
              </Button>
              <Button
                onClick={() => {
                  void run({ type: 'ticket/reply', ticketId: selected.id, body: 'Wir prüfen das und melden uns zeitnah.', resolve: false });
                  setSelected(null);
                }}
              >
                Antworten
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  void run({ type: 'ticket/reply', ticketId: selected.id, body: 'Das Anliegen ist erledigt.', resolve: true });
                  setSelected(null);
                }}
              >
                Lösen und schließen
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Priorität">
                <Badge tone={PRIORITY_TONE[selected.priority]}>{PRIORITY_LABEL[selected.priority]}</Badge>
              </Field>
              <Field label="Kategorie">{selected.category}</Field>
              <Field label="Zielzeit">{duration(RESPONSE_SLA[selected.priority])}</Field>
              <Field label="Alter">{duration(world.meta.tick - selected.openedAtTick)}</Field>
            </div>

            <TicketSubject ticket={selected} />

            <div className="space-y-2">
              {selected.messages.map((message, index) => (
                <div
                  key={index}
                  className={`rounded-md p-3 text-sm ${
                    message.author === 'customer'
                      ? 'bg-panel-100 dark:bg-panel-950'
                      : message.author === 'system'
                        ? 'bg-sky-50 dark:bg-sky-950/40'
                        : 'bg-accent-50 dark:bg-accent-950/40'
                  }`}
                >
                  <p className="mb-1 text-2xs uppercase tracking-wide text-panel-500">
                    {message.author === 'customer' ? 'Kunde' : message.author === 'system' ? 'System' : 'Support'} · Tick {num(message.tick)}
                  </p>
                  <p>{message.body}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-2 border-t divider pt-3">
              {(['low', 'normal', 'high', 'urgent'] as TicketPriority[]).map((priority) => (
                <Button
                  key={priority}
                  size="sm"
                  variant={selected.priority === priority ? 'primary' : 'secondary'}
                  onClick={() => void run({ type: 'ticket/setPriority', ticketId: selected.id, priority })}
                >
                  {PRIORITY_LABEL[priority]}
                </Button>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Finanzen
// ---------------------------------------------------------------------------

const LEDGER_LABEL: Record<string, string> = {
  'revenue-hosting': 'Hosting',
  'revenue-domain': 'Domains',
  'revenue-colocation': 'Colocation',
  'revenue-transit': 'Transit',
  'revenue-other': 'Sonstige Erlöse',
  'cost-power': 'Strom',
  'cost-cooling': 'Kühlung',
  'cost-rack': 'Racks und Miete',
  'cost-hardware': 'Hardware',
  'cost-staff': 'Personal',
  'cost-transit': 'IP-Transit',
  'cost-ip': 'IP-Adressen',
  'cost-fiber': 'Glasfaser',
  'cost-ixp': 'IXP',
  'cost-research': 'Forschung',
  'cost-domain': 'Domainkosten',
  'cost-penalty': 'Gutschriften',
  'cost-loan': 'Kreditraten',
  'cost-tax': 'Umsatzsteuer',
  'cost-other': 'Sonstiges',
};

export function Finance() {
  const metrics = useMetrics();
  const gameId = useGame((s) => s.gameId);
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [ledger, setLedger] = useState<NonNullable<typeof world>['ledger']>([]);
  const [repay, setRepay] = useState('1000');
  const [loanProducts, setLoanProducts] = useState<LoanProductTemplate[]>([]);
  const [loanAmounts, setLoanAmounts] = useState<Record<string, string>>({});
  const [extraRepay, setExtraRepay] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!gameId) return;
    void api.ledger(gameId, 400).then(setLedger).catch(() => setLedger([]));
  }, [gameId, metrics?.meta.tick]);

  useEffect(() => {
    if (!gameId) return;
    void api.catalog(gameId).then((c) => setLoanProducts(c.loanProducts)).catch(() => setLoanProducts([]));
  }, [gameId]);

  if (!metrics || !world) return null;
  const { derived, company } = metrics;
  const series = world.monitoring.series;
  const loans = Object.values(world.loans).sort((a, b) => b.issuedAtTick - a.issuedAtTick);
  const activeLoans = loans.filter((l) => l.state === 'active');

  const columns: Column<(typeof ledger)[number]>[] = [
    { key: 'tick', header: 'Tick', align: 'right', sortValue: (e) => e.tick, render: (e) => <span className="tabular text-xs">{num(e.tick)}</span> },
    { key: 'category', header: 'Kategorie', sortValue: (e) => e.category, render: (e) => <span className="text-xs">{LEDGER_LABEL[e.category] ?? e.category}</span> },
    { key: 'description', header: 'Buchungstext', render: (e) => <span className="text-xs">{e.description}</span> },
    {
      key: 'amount',
      header: 'Betrag',
      align: 'right',
      sortValue: (e) => e.amount,
      render: (e) => (
        <span className={`tabular text-xs font-medium ${e.amount >= 0 ? 'text-accent-600 dark:text-accent-400' : 'text-red-600 dark:text-red-400'}`}>
          {money(e.amount)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Finanzen" description="Ertragslage, Kostenstruktur und Buchungen." />

      <StatGrid cols={4}>
        <Stat label="Liquidität" value={money(company.cash)} tone={company.cash < 0 ? 'negative' : 'neutral'} />
        <Stat label="Umsatz/Monat" value={money(derived.finance.monthlyRevenue)} tone="positive" />
        <Stat label="Kosten/Monat" value={money(derived.finance.monthlyCost)} tone="negative" />
        <Stat
          label="Ergebnis/Monat"
          value={money(derived.finance.monthlyProfit)}
          tone={derived.finance.monthlyProfit >= 0 ? 'positive' : 'negative'}
          hint={
            derived.finance.monthlyRevenue > 0
              ? `Marge ${pct100((derived.finance.monthlyProfit / derived.finance.monthlyRevenue) * 100, 0)}`
              : undefined
          }
        />
      </StatGrid>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card title="Liquiditätsverlauf" className="lg:col-span-2">
          <LineChart series={[{ label: 'Liquidität', points: series.cash?.points ?? [], color: company.cash < 0 ? '#ef4444' : '#21a471' }]} height={200} unit="€" />
        </Card>

        <Card title="Kredit">
          <div className="space-y-3">
            <Field label="Kreditrahmen">{money(company.creditLimit)}</Field>
            <Field label="In Anspruch genommen">{money(company.debt)}</Field>
            <Meter value={company.debt} max={company.creditLimit} label={<><span>Auslastung</span><span className="tabular">{pct100((company.debt / Math.max(1, company.creditLimit)) * 100, 0)}</span></>} />
            <div className="flex items-end gap-2">
              <Input label="Tilgung (€)" value={repay} onChange={(e) => setRepay(e.target.value)} className="w-32" />
              <Button onClick={() => void run({ type: 'finance/repayDebt', amount: Number(repay) || 0 })}>Tilgen</Button>
            </div>
            <p className="text-2xs text-panel-500">
              Gesamtumsatz seit Gründung: {money(company.lifetimeRevenue)} · Gesamtkosten: {money(company.lifetimeCost)}
            </p>
          </div>
        </Card>
      </div>

      <Card className="mt-4" title="Bankprodukte" subtitle="Feste Summe, fester Zins, feste Laufzeit – anders als die Kreditlinie oben.">
        {loanProducts.length === 0 ? (
          <p className="text-xs text-panel-500">Keine Bankprodukte verfügbar.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {loanProducts.map((product) => {
              const rate = effectiveLoanRate(world, product);
              const locked = company.reputation < product.minReputation;
              const amount = loanAmounts[product.key] ?? String(product.minAmount);
              return (
                <div key={product.key} className="surface-muted flex flex-col gap-2 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{product.name}</span>
                    <Badge tone="muted">{pct100(rate * 100, 1)} p. a.</Badge>
                  </div>
                  <p className="text-2xs text-panel-500 dark:text-panel-400">{product.description}</p>
                  <p className="text-2xs text-panel-500 dark:text-panel-400">
                    {money(product.minAmount)} – {money(product.maxAmount)} · Laufzeit {Math.round(product.termTicks / SIM.TICKS_PER_YEAR)} Jahre
                  </p>
                  {locked ? (
                    <Notice tone="warning">Ruf zu niedrig – mindestens {product.minReputation}, aktuell {Math.round(company.reputation)}.</Notice>
                  ) : (
                    <div className="mt-1 flex items-end gap-2">
                      <Input
                        label="Betrag (€)"
                        value={amount}
                        onChange={(e) => setLoanAmounts((prev) => ({ ...prev, [product.key]: e.target.value }))}
                        className="w-32"
                      />
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => void run({ type: 'bank/takeLoan', productKey: product.key, amount: Number(amount) || 0 })}
                      >
                        Aufnehmen
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {loans.length > 0 && (
        <Card className="mt-4" title="Laufende Kredite">
          <div className="space-y-3">
            {loans.map((loan) => {
              const extra = extraRepay[loan.id] ?? '';
              return (
                <div key={loan.id} className="surface-muted flex flex-wrap items-center gap-3 p-2.5 text-xs">
                  <Badge tone={loan.state === 'paid' ? 'success' : 'info'}>{loan.state === 'paid' ? 'getilgt' : 'läuft'}</Badge>
                  <span className="min-w-0 flex-1 truncate font-medium">{loan.name}</span>
                  <span className="tabular text-panel-500">{pct100(loan.interestRate * 100, 1)} p. a.</span>
                  <span className="tabular">{money(loan.balance)} Restschuld</span>
                  <span className="tabular text-panel-500">noch {duration(loan.remainingTicks)}</span>
                  {loan.state === 'active' && (
                    <>
                      <Input
                        value={extra}
                        onChange={(e) => setExtraRepay((prev) => ({ ...prev, [loan.id]: e.target.value }))}
                        className="w-28"
                        placeholder="Sondertilgung €"
                      />
                      <Button size="sm" onClick={() => void run({ type: 'bank/repayLoan', loanId: loan.id, amount: Number(extra) || 0 })}>
                        Tilgen
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Kosten nach Kategorie" subtitle="Hochrechnung auf einen Monat">
          <BarBreakdown
            items={Object.entries(derived.finance.costBreakdown)
              .map(([key, value]) => ({ label: LEDGER_LABEL[key] ?? key, value }))
              .sort((a, b) => b.value - a.value)}
            formatValue={(v) => money(v)}
          />
        </Card>
        <Card title="Erlöse nach Kategorie">
          <BarBreakdown
            items={Object.entries(derived.finance.revenueBreakdown)
              .map(([key, value]) => ({ label: LEDGER_LABEL[key] ?? key, value }))
              .sort((a, b) => b.value - a.value)}
            formatValue={(v) => money(v)}
            emptyLabel="Noch keine Zahlungseingänge."
          />
        </Card>
      </div>

      <Card className="mt-4" title="Hauptbuch" padded={false}>
        <DataTable columns={columns} rows={ledger} rowKey={(e, ) => `${e.tick}-${e.category}-${e.description}-${e.amount}`} dense maxHeight="max-h-[28rem]" empty="Keine Buchungen." />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Personal
// ---------------------------------------------------------------------------

const STAFF_ROLE_LABEL: Record<StaffRole, string> = {
  technician: 'Techniker',
  'network-engineer': 'Netzwerktechnik',
  support: 'Support',
  sales: 'Vertrieb',
  security: 'Sicherheit',
};

export function StaffPage() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [hiring, setHiring] = useState(false);
  const [role, setRole] = useState<StaffRole>('support');
  const [skill, setSkill] = useState('5');
  const [level, setLevel] = useState('1');
  const [shift, setShift] = useState<ShiftModel>('day');
  const [specialties, setSpecialties] = useState<TicketCategory[]>(DEFAULT_SPECIALTIES.support ?? []);

  if (!world) return null;
  const staff = Object.values(world.staff);
  const monthly = staff.reduce((s, m) => s + m.monthlySalary, 0);

  const columns: Column<Staff>[] = [
    { key: 'name', header: 'Name', sortValue: (s) => s.name, render: (s) => <span className="font-medium">{s.name}</span> },
    { key: 'role', header: 'Rolle', sortValue: (s) => s.role, render: (s) => <Badge tone="neutral">{STAFF_ROLE_LABEL[s.role]}</Badge> },
    {
      key: 'skill',
      header: 'Erfahrung',
      align: 'right',
      sortValue: (s) => s.skill,
      render: (s) => (
        <div className="flex items-center justify-end gap-2">
          <span className="tabular text-xs">{s.skill}/10</span>
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-panel-200 dark:bg-panel-800">
            <div className="h-full rounded-full bg-accent-500" style={{ width: `${s.skill * 10}%` }} />
          </div>
        </div>
      ),
    },
    {
      key: 'level',
      header: 'Stufe',
      sortValue: (s) => s.level,
      render: (s) => (s.role === 'support' ? <Badge tone={s.level > 1 ? 'info' : 'muted'}>{s.level}. Level</Badge> : '–'),
    },
    {
      key: 'shift',
      header: 'Arbeitszeit',
      sortValue: (s) => s.shift,
      render: (s) => (
        <Select
          value={s.shift}
          onChange={(e) => void run({ type: 'staff/setShift', staffId: s.id, shift: e.target.value as ShiftModel })}
          className="text-xs"
        >
          {Object.entries(SHIFT_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: 'specialties',
      header: 'Fachgebiete',
      hideOnMobile: true,
      render: (s) => (
        <span className="text-2xs text-panel-500 dark:text-panel-400">
          {s.specialties.length > 0 ? s.specialties.map((c) => TICKET_CATEGORY_LABEL[c]).join(', ') : 'keine'}
        </span>
      ),
    },
    { key: 'site', header: 'Standort', hideOnMobile: true, render: (s) => (s.datacenterId ? world.datacenters[s.datacenterId]?.city : 'übergreifend') },
    { key: 'salary', header: 'Gehalt/Monat', align: 'right', sortValue: (s) => s.monthlySalary, render: (s) => <span className="tabular">{money(s.monthlySalary)}</span> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (s) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            if (confirm(`${s.name} entlassen? Es fallen zwei Monatsgehälter Abfindung an.`)) void run({ type: 'staff/fire', staffId: s.id });
          }}
        >
          entlassen
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Personal"
        description="Techniker reparieren Hardware selbstständig, Support bearbeitet Tickets."
        actions={
          <Button variant="primary" onClick={() => setHiring(true)}>
            Einstellen
          </Button>
        }
      />

      <StatGrid cols={5}>
        <Stat label="Mitarbeitende" value={num(staff.length)} />
        <Stat label="Personalkosten" value={money(monthly)} hint="pro Monat" />
        <Stat
          label="Support"
          value={num(staff.filter((s) => s.role === 'support').length)}
          tone={staff.filter((s) => s.role === 'support').length === 0 ? 'negative' : 'neutral'}
          hint="je etwa 1.000 Kunden tragbar"
        />
        <Stat
          label="Gerade im Dienst"
          value={pct100(supportCapacity(world).coverage * 100, 0)}
          tone={supportCapacity(world).coverage === 0 ? 'negative' : 'neutral'}
          hint="Anteil des Supports, der zur aktuellen Stunde arbeitet"
        />
        <Stat
          label="Technik"
          value={num(staff.filter((s) => s.role === 'technician' || s.role === 'network-engineer').length)}
          tone={staff.filter((s) => s.role === 'technician').length === 0 ? 'warning' : 'neutral'}
          hint="repariert defekte Geräte"
        />
      </StatGrid>

      {staff.filter((s) => s.role === 'technician' || s.role === 'network-engineer').length === 0 && (
        <Notice tone="warning" title="Kein Techniker im Team">
          Ohne technisches Personal bleibt defekte Hardware liegen, bis sie von Hand repariert wird. Ein einzelner Routerausfall kann das
          gesamte Netz für Tage lahmlegen.
        </Notice>
      )}

      <Card className="mt-4" padded={false}>
        <DataTable columns={columns} rows={staff} rowKey={(s) => s.id} empty="Kein Personal eingestellt." />
      </Card>

      <Modal
        open={hiring}
        onClose={() => setHiring(false)}
        title="Neue Mitarbeitende einstellen"
        footer={
          <>
            <Button variant="ghost" onClick={() => setHiring(false)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                const result = await run({
                  type: 'staff/hire',
                  role,
                  skill: Number(skill),
                  datacenterId: null,
                  level: Number(level) as 1 | 2 | 3,
                  shift,
                  specialties,
                });
                if (result.ok) setHiring(false);
              }}
            >
              Einstellen
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Select label="Rolle" value={role} onChange={(e) => setRole(e.target.value as StaffRole)}>
            {Object.entries(STAFF_ROLE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select label="Erfahrung" value={skill} onChange={(e) => setSkill(e.target.value)}>
            {[2, 4, 5, 6, 8, 10].map((value) => (
              <option key={value} value={value}>
                Stufe {value} – {money(1900 + value * 350)} pro Monat
              </option>
            ))}
          </Select>
          {role === 'support' && (
            <Select label="Supportstufe" value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="1">1st Level – nimmt auf und löst Einfaches</option>
              <option value="2">2nd Level – übernimmt, was hängen bleibt (+35 % Gehalt)</option>
              <option value="3">3rd Level – die harten Fälle (+70 % Gehalt)</option>
            </Select>
          )}

          <Select label="Arbeitszeitmodell" value={shift} onChange={(e) => setShift(e.target.value as ShiftModel)}>
            <option value="day">Bürozeiten – 8 bis 18 Uhr, nachts niemand</option>
            <option value="oncall">Rufbereitschaft – nachts eingeschränkt (+25 % Gehalt)</option>
            <option value="shift">Schichtbetrieb – rund um die Uhr (+70 % Gehalt)</option>
          </Select>

          <div>
            <p className="label mb-1">Fachgebiete</p>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(TICKET_CATEGORY_LABEL) as TicketCategory[]).map((category) => (
                <button
                  key={category}
                  type="button"
                  className={clsx(
                    'rounded border px-2 py-1 text-xs transition-colors',
                    specialties.includes(category)
                      ? 'border-accent-500 bg-accent-600/10 text-accent-700 dark:text-accent-300'
                      : 'divider text-panel-500 dark:text-panel-400',
                  )}
                  onClick={() =>
                    setSpecialties((prev) =>
                      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
                    )
                  }
                >
                  {TICKET_CATEGORY_LABEL[category]}
                </button>
              ))}
            </div>
            <p className="mt-1 text-2xs text-panel-500 dark:text-panel-400">
              Fachfremde Vorgänge dauern länger und scheitern häufiger – und wandern dann eine Stufe höher.
            </p>
          </div>

          <Notice tone="info">
            Erfahrenere Kräfte lösen Tickets zuverlässiger und reparieren schneller. Ohne Rufbereitschaft oder Schichtbetrieb
            bleibt nachts alles liegen. Bei der Einstellung wird ein Monatsgehalt als Einstellungskosten fällig.
          </Notice>
        </div>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Ticketakte: woran hängt der Vorgang?
// ---------------------------------------------------------------------------

/**
 * Produkt und Knoten in einer Tabellenzeile – knapp, weil daneben schon
 * Betreff, Priorität und Alter stehen.
 */
function TicketSubjectCell({ ticket }: { ticket: Ticket }) {
  const world = useGame((s) => s.world);
  if (!world) return null;
  const services = ticketServices(world, ticket);

  if (services.length === 0) return <span className="text-2xs text-panel-400">kein Dienst</span>;
  if (services.length > 1) {
    return <span className="text-xs text-panel-500">{services.length} Dienste</span>;
  }

  const [service] = services;
  return (
    <div className="min-w-0">
      <p className="truncate text-xs">{service.productName}</p>
      <p className="truncate text-2xs text-panel-500">{service.serverName ?? 'im Pool'}</p>
    </div>
  );
}

/**
 * Dieselbe Auskunft im geöffneten Vorgang, diesmal vollständig.
 *
 * Hier stehen alle Dienste des Kunden statt eines herausgegriffenen: Wer ein
 * Ticket bearbeitet, will wissen, worauf er schauen muss – und bei drei
 * Verträgen ist die Antwort eben „auf diese drei“ und nicht „vermutlich auf den
 * ersten“.
 */
function TicketSubject({ ticket }: { ticket: Ticket }) {
  const world = useGame((s) => s.world);
  if (!world || !hasTicketContext(world)) return null;

  const services = ticketServices(world, ticket);
  if (services.length === 0) {
    return (
      <div className="border-t divider pt-3 text-xs text-panel-500 dark:text-panel-400">
        Zu diesem Vorgang gehört kein aktiver Dienst – eine Anfrage ohne laufenden Vertrag.
      </div>
    );
  }

  return (
    <div className="border-t divider pt-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-panel-500 dark:text-panel-400">
        Betroffene Dienste
      </h3>
      <ul className="space-y-1">
        {services.map((service) => (
          <li key={service.contractId} className="surface-muted flex flex-wrap items-center gap-3 p-2 text-xs">
            <Badge tone="muted">{service.productKind}</Badge>
            <span className="min-w-0 flex-1 truncate font-medium">{service.productName}</span>
            {service.serverName ? (
              <span className="text-panel-500">
                {service.serverName}
                {service.datacenterCity ? ` · ${service.datacenterCity}` : ''}
              </span>
            ) : (
              // Ein Pool-Vertrag liegt auf mehreren Knoten; „der Server“ wäre
              // hier eine Antwort auf eine Frage, die sich nicht stellt.
              <span className="text-panel-400">hinter einem Lastausgleich</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
